import classNames from "classnames";
import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import Modal from "react-modal";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { FinancingModalTrigger } from "../../apps/financing/FinancingModalTrigger";
import { useFlickity } from "../../hooks/useFlickity";
import { WebPageURL } from "../../models/nominals";
import { IServiceFooterValueProps } from "../../models/value-props";
import { usePageSetting } from "../../utils/settings";
import { ContactUsModal } from "../ContactUsModal";
import type { IEmailModalSnippet } from "../EmailCaptureContent";
import { RichText } from "../RichText";
import { ServiceFooterValuePropsModal } from "../ServiceFooterValuePropsModal";

import styles from "./index.module.scss";

// Dynamic import type for EmailModal (depends on cleave-zen which causes
// ERR_REQUIRE_CYCLE_MODULE during SSR bundling)
type EmailModalType = typeof import("../EmailModal").EmailModal;

Modal.defaultStyles = {
    overlay: {},
    content: {},
};

interface IServiceFooterSnippetProps {
    title: string;
    type: string;
    icon?: string;
    content: IServiceFooterValueProps | IEmailModalSnippet | null;
    trigger: string;
    trigger_mobile: string;
}

const renderSnippetContent = (
    trigger: IServiceFooterSnippetProps,
    icon?: string,
): React.JSX.Element => {
    const hasMobileContent = trigger.trigger_mobile.length > 0;

    return (
        <>
            {icon && <img alt="" src={icon} loading="lazy" decoding="async" />}
            <RichText
                inline={true}
                html={trigger.trigger}
                className={hasMobileContent ? styles.desktopContent : undefined}
            />
            {hasMobileContent && (
                <RichText
                    inline={true}
                    html={trigger.trigger_mobile}
                    className={styles.mobileContent}
                />
            )}
        </>
    );
};

const renderSnippetButton = (
    isCarousel: boolean,
    snippet: Immutable<IServiceFooterSnippetProps>,
    onClick: React.MouseEventHandler<HTMLButtonElement> | undefined,
    slideIndex: number,
    activeCarouselSlideIndex: number,
): React.JSX.Element => {
    const classes = classNames({
        [styles.trigger]: true,
        [styles.carouselTrigger]: isCarousel,
    });
    const slideHidden = isCarousel && slideIndex !== activeCarouselSlideIndex;

    return (
        <button
            className={classes}
            onClick={onClick ? onClick : undefined}
            tabIndex={slideHidden ? -1 : undefined}
        >
            {renderSnippetContent(snippet, snippet.icon)}
        </button>
    );
};

export const ServiceFooter = (
    props: Immutable<{
        interval: number | null;
        loops: number | null;
        snippets: IServiceFooterSnippetProps[];
        chatOfflineLink?: WebPageURL;
    }>,
): React.ReactNode => {
    const snippetsElemRef = React.useRef<HTMLDivElement>(null);

    const [modalContactIsOpen, setModalContactIsOpen] = React.useState(false);
    const [modalPropsIsOpen, setModalPropsIsOpen] = React.useState(false);
    const [modalEmailIsOpen, setModalEmailIsOpen] = React.useState(false);
    /** Synced with Flickity `select` — WCAG 4.1.2 / A360 #292 (hidden slides not tabbable). */
    const [activeCarouselSlideIndex, setActiveCarouselSlideIndex] =
        React.useState(0);
    const footerSettings = usePageSetting("footer_settings");

    React.useEffect(() => {
        setActiveCarouselSlideIndex(0);
    }, [props.snippets.length]);

    const onCarouselInit = React.useCallback(
        (flickity: Flickity) => {
            setActiveCarouselSlideIndex(flickity.selectedIndex);

            const onSelect = (): void => {
                setActiveCarouselSlideIndex(flickity.selectedIndex);
            };
            flickity.on("select", onSelect);

            const window = getWindow();
            let timerId: number | null = null;

            if (props.loops) {
                const carouselInterval = props.interval ? props.interval : 3000;
                const duration =
                    carouselInterval * props.snippets.length * props.loops;

                if (window) {
                    timerId = window.setInterval(() => {
                        flickity.stopPlayer();
                    }, duration);
                }
            }

            const onMouseover = () => flickity.stopPlayer();
            const onFocus = () => flickity.stopPlayer();
            const el = snippetsElemRef.current;

            el?.addEventListener("mouseover", onMouseover);
            el?.addEventListener("focus", onFocus);

            return () => {
                flickity.off("select", onSelect);
                if (timerId && window) {
                    window.clearInterval(timerId);
                }
                el?.removeEventListener("mouseover", onMouseover);
                el?.removeEventListener("focus", onFocus);
            };
        },
        [props.interval, props.loops, props.snippets.length],
    );

    // Re-initialization is driven by onCarouselInit's deps (interval, loops, snippets.length)
    useFlickity(snippetsElemRef, {
        options: {
            cellAlign: "center",
            pageDots: false,
            prevNextButtons: true,
            imagesLoaded: true,
            arrowShape: {
                x0: 20,
                x1: 60,
                y1: 50,
                x2: 60,
                y2: 30,
                x3: 35,
            },
            autoPlay: props.interval ? props.interval : true,
            wrapAround: true,
        },
        enabled: props.snippets.length > 1,
        onInit: onCarouselInit,
    });

    // Dynamically load EmailModal (depends on cleave-zen via EmailCaptureContent)
    const [emailModuleLoaded, setEmailModuleLoaded] = React.useState(false);
    const emailModalRef = React.useRef<EmailModalType | null>(null);

    React.useEffect(() => {
        void import("../EmailModal")
            .then((module) => {
                emailModalRef.current = module.EmailModal;
                setEmailModuleLoaded(true);
            })
            .catch(() => {
                // Fail silently — email modal button will remain non-functional
                console.error("Failed to load EmailModal module");
            });
    }, []);

    const onOpenModalContact = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
        }

        setModalContactIsOpen(true);
    };

    const onOpenModalProps = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
        }

        setModalPropsIsOpen(true);
    };

    const onOpenModalEmail = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
        }

        setModalEmailIsOpen(true);
    };

    const onCloseModalContact = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        setModalContactIsOpen(false);
    };

    const onCloseModalProps = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        setModalPropsIsOpen(false);
    };

    const onCloseModalEmail = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        setModalEmailIsOpen(false);
    };

    // Render only buttons inside the carousel — no modals.
    // Flickity rearranges DOM children, so React must not insert/remove
    // elements inside the carousel after initial render.
    const renderSnippets = (): React.JSX.Element[] => {
        const isCarousel = props.snippets.length > 1;

        return props.snippets.map((snippet, i) => {
            const slideHidden = isCarousel && i !== activeCarouselSlideIndex;
            const slideWrapper = (
                child: React.ReactNode,
            ): React.JSX.Element => (
                <div
                    key={i}
                    className={styles.carouselSlide}
                    aria-hidden={isCarousel ? slideHidden : false}
                >
                    {child}
                </div>
            );

            switch (snippet.type) {
                case "default":
                    return slideWrapper(
                        renderSnippetButton(
                            isCarousel,
                            snippet,
                            undefined,
                            i,
                            activeCarouselSlideIndex,
                        ),
                    );
                case "email":
                    return slideWrapper(
                        renderSnippetButton(
                            isCarousel,
                            snippet,
                            onOpenModalEmail,
                            i,
                            activeCarouselSlideIndex,
                        ),
                    );
                case "prequal": {
                    const classes = classNames({
                        [styles.financingModalTrigger]: true,
                        "al-service-footer-finance-block__pre-qualify": true,
                        [styles.carouselTrigger]: isCarousel,
                    });
                    return slideWrapper(
                        <FinancingModalTrigger
                            modalType="prequal-app"
                            className={classes}
                            tabIndex={slideHidden ? -1 : undefined}
                        >
                            {renderSnippetContent(snippet, snippet.icon)}
                        </FinancingModalTrigger>,
                    );
                }
                case "value_prop":
                    return slideWrapper(
                        renderSnippetButton(
                            isCarousel,
                            snippet,
                            onOpenModalProps,
                            i,
                            activeCarouselSlideIndex,
                        ),
                    );
                default:
                    throw new Error(
                        t`Unexpected service footer snippet type: ${snippet.type}`,
                    );
            }
        });
    };

    // Find snippet data for modals rendered outside the carousel
    const emailSnippet = props.snippets.find((s) => s.type === "email");
    const valuePropSnippet = props.snippets.find(
        (s) => s.type === "value_prop",
    );

    const contactUs = !!footerSettings && (
        <div
            className={`${styles.contactUs} service-footer__contact-us al-service-footer__buttons`}
        >
            <button onClick={onOpenModalContact}>{t`Contact Us`}</button>
        </div>
    );

    const EmailModal = emailModalRef.current;

    return (
        <div className="service-footer-container">
            <div
                className={`${styles.root} service-footer al-service-footer`}
                role="region"
                aria-label={t`Help and support`}
            >
                <div className={`${styles.content} l-capped-width`}>
                    <div className={styles.snippetsWrapper}>
                        <div
                            className={styles.snippets}
                            ref={(ref) => {
                                snippetsElemRef.current = ref;
                            }}
                        >
                            {renderSnippets()}
                        </div>
                    </div>
                    {contactUs}
                </div>
                {/* All modals rendered outside the carousel to avoid Flickity DOM conflicts */}
                {emailModuleLoaded && EmailModal && emailSnippet && (
                    <EmailModal
                        isOpen={modalEmailIsOpen}
                        onClose={onCloseModalEmail}
                        content={emailSnippet.content as IEmailModalSnippet}
                    />
                )}
                {valuePropSnippet && (
                    <ServiceFooterValuePropsModal
                        isOpen={modalPropsIsOpen}
                        onClose={onCloseModalProps}
                        content={
                            valuePropSnippet.content as IServiceFooterValueProps
                        }
                    />
                )}
                <ContactUsModal
                    isOpen={modalContactIsOpen}
                    onClose={onCloseModalContact}
                    chatOfflineLink={props.chatOfflineLink}
                />
                <div className={styles.iphoneXbg}></div>
            </div>
        </div>
    );
};
